/**
 * État du workspace d'une évaluation : sélection du document, timer, scroll,
 * statut de lecture, headings, annotations en cours, amendments en attente.
 *
 * @remarks
 * Découpé du legacy. Mélange persisté (readingStatus, openedDocuments,
 * pendingAmendments) et éphémère (sélection, scroll position, heading actif,
 * annotations focus). La séparation se fait dans `partialize`.
 */

import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import type { PendingAnnotationCapture, ReadingStatus } from '../types';

interface WorkspaceState {
  // ── Sélection (éphémère, reset à la navigation) ──────────────────────────
  selectedDocumentId: string | null;

  // ── Timer (persisté pour reprise après reload pendant l'eval) ────────────
  evaluationStartTime: number | null;
  evaluationTimerPaused: boolean;

  // ── Reading status (persisté) ────────────────────────────────────────────
  readingStatus: Record<string, ReadingStatus>;
  scrollPercent: number; // éphémère
  openedDocuments: Record<string, true>; // persisté — efface badge UPDATED

  // ── Heading tracking (éphémère) ──────────────────────────────────────────
  activeHeadingId: string | null;
  scrollToHeadingId: string | null;

  // ── Annotations focus (éphémère) ─────────────────────────────────────────
  scrollToAnnotationId: string | null;
  focusAnnotationId: string | null;
  activeAnnotationId: string | null;
  pendingAnnotation: PendingAnnotationCapture | null;

  // ── Amendments (persisté — confirmAmendment retire de la liste) ──────────
  pendingAmendments: string[];

  // ── Document contents (éphémère, chargés à la demande) ───────────────────
  documentContents: Record<string, string>;

  // ── Actions ──────────────────────────────────────────────────────────────
  setSelectedDocument: (documentId: string | null) => void;

  startEvaluationTimer: (evaluationId: string) => void;
  pauseEvaluationTimer: () => void;
  resumeEvaluationTimer: () => void;

  setScrollPercent: (percent: number) => void;
  markDocumentOpened: (documentId: string) => void;

  setActiveHeadingId: (id: string | null) => void;
  setScrollToHeadingId: (id: string | null) => void;
  setScrollToAnnotationId: (id: string | null) => void;
  setFocusAnnotationId: (id: string | null) => void;
  setActiveAnnotationId: (id: string | null) => void;
  setPendingAnnotation: (capture: PendingAnnotationCapture | null) => void;

  confirmAmendment: (documentId: string) => void;

  setDocumentContent: (documentId: string, content: string) => void;
}

const SEED_READING_STATUS: Record<string, ReadingStatus> = {
  'playwright-guide-01': {
    documentId: 'playwright-guide-01',
    status: 'in-progress',
    maxScrollPercent: 55,
  },
  'testing-patterns-01': {
    documentId: 'testing-patterns-01',
    status: 'read',
    maxScrollPercent: 100,
  },
  'vitest-setup-01': {
    documentId: 'vitest-setup-01',
    status: 'in-progress',
    maxScrollPercent: 30,
  },
  'test-strategy-01': {
    documentId: 'test-strategy-01',
    status: 'read',
    maxScrollPercent: 100,
  },
};

export const useEvaluationWorkspaceStore = create<WorkspaceState>()(
  devtools(
    persist(
      (set) => ({
        selectedDocumentId: null,
        evaluationStartTime: null,
        evaluationTimerPaused: false,

        readingStatus: SEED_READING_STATUS,
        scrollPercent: 0,
        openedDocuments: {},

        activeHeadingId: null,
        scrollToHeadingId: null,
        scrollToAnnotationId: null,
        focusAnnotationId: null,
        activeAnnotationId: null,
        pendingAnnotation: null,

        pendingAmendments: ['test-strategy-01'],

        documentContents: {},

        setSelectedDocument: (documentId) => {
          set({ selectedDocumentId: documentId });
        },

        startEvaluationTimer: (_evaluationId) => {
          set({ evaluationStartTime: Date.now(), evaluationTimerPaused: false });
        },

        pauseEvaluationTimer: () => {
          set({ evaluationTimerPaused: true });
        },

        resumeEvaluationTimer: () => {
          set({ evaluationTimerPaused: false });
        },

        setScrollPercent: (percent) => {
          set((state) => {
            const { selectedDocumentId, readingStatus } = state;
            if (!selectedDocumentId) return { scrollPercent: percent };

            const current = readingStatus[selectedDocumentId];
            const maxScroll = Math.max(current?.maxScrollPercent ?? 0, percent);
            const currentStatus = current?.status ?? 'unread';

            let newStatus = currentStatus;
            if (currentStatus !== 'read') {
              if (maxScroll >= 80) newStatus = 'read';
              else if (percent > 0) newStatus = 'in-progress';
            }

            return {
              scrollPercent: percent,
              readingStatus: {
                ...readingStatus,
                [selectedDocumentId]: {
                  documentId: selectedDocumentId,
                  status: newStatus,
                  maxScrollPercent: maxScroll,
                },
              },
            };
          });
        },

        markDocumentOpened: (documentId) => {
          set((state) => ({
            openedDocuments: { ...state.openedDocuments, [documentId]: true as const },
          }));
        },

        setActiveHeadingId: (id) => {
          set({ activeHeadingId: id });
        },
        setScrollToHeadingId: (id) => {
          set({ scrollToHeadingId: id });
        },
        setScrollToAnnotationId: (id) => {
          set({ scrollToAnnotationId: id });
        },
        setFocusAnnotationId: (id) => {
          set({ focusAnnotationId: id });
        },
        setActiveAnnotationId: (id) => {
          set({ activeAnnotationId: id });
        },
        setPendingAnnotation: (capture) => {
          set({ pendingAnnotation: capture });
        },

        confirmAmendment: (documentId) => {
          set((state) => ({
            pendingAmendments: state.pendingAmendments.filter((id) => id !== documentId),
          }));
        },

        setDocumentContent: (documentId, content) => {
          set((state) => ({
            documentContents: { ...state.documentContents, [documentId]: content },
          }));
        },
      }),
      {
        name: 'saas-front-v2:evaluation-workspace',
        version: 1,
        partialize: (state) => ({
          evaluationStartTime: state.evaluationStartTime,
          evaluationTimerPaused: state.evaluationTimerPaused,
          readingStatus: state.readingStatus,
          openedDocuments: state.openedDocuments,
          pendingAmendments: state.pendingAmendments,
        }),
        /**
         * Ce store est en `version: 1` depuis sa création : aucune version
         * antérieure n'existe. `migrate` n'est donc atteint que sur un payload
         * d'origine inconnue, dont la forme n'est pas garantie — on repart d'un
         * workspace **vide**, timer remis à zéro.
         *
         * Le fallback ne doit surtout pas valoir `SEED_READING_STATUS` ni
         * `['test-strategy-01']` : le candidat verrait des documents marqués lus
         * qu'il n'a pas ouverts et un amendment fabriqué, et zustand réécrit le
         * résultat de la migration dans le storage.
         *
         * ⚠️ Au prochain bump, ajouter la transformation **avant** ce retour et
         * comparer avec `<` : `if (oldVersion < 2) { …adapter… }`. Ne jamais
         * tester `oldVersion === <version courante>` — zustand n'appelle
         * `migrate` que si les versions diffèrent, la branche serait morte.
         * Motif en cascade de référence : `use-layout-store`. (SAAS-903)
         */
        migrate: () => ({
          evaluationStartTime: null,
          evaluationTimerPaused: false,
          readingStatus: {},
          openedDocuments: {},
          pendingAmendments: [],
        }),
      },
    ),
    { name: 'evaluation-workspace', enabled: import.meta.env.DEV },
  ),
);
